// Utility functions for shelf mapping suggestions

export interface ShelfSuggestion {
  type: 'system_shelf' | 'custom_shelf' | 'tag' | 'skip';
  value: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface ShelfAnalysis {
  name: string;
  bookCount: number;
  isExclusive: boolean;
  suggestedMapping: ShelfSuggestion;
}

export interface AnalysisResult {
  totalBooks: number;
  exclusiveShelves: ShelfAnalysis[];
  customShelves: ShelfAnalysis[];
  sampleBooks: Array<{ title: string; author: string; shelf: string }>;
}

export interface ShelfMapping {
  goodreadsShelf: string;
  targetType: 'system_shelf' | 'custom_shelf' | 'tag' | 'skip';
  targetValue: string;
  bookCount: number;
}

// System shelves available in Dino
export const SYSTEM_SHELVES = [
  { value: 'to-read', label: 'Want to Read (TBR)', icon: '📚' },
  { value: 'reading', label: 'Currently Reading', icon: '📖' },
  { value: 'read', label: 'Read / Finished', icon: '✅' },
  { value: 'unfinished', label: 'Did Not Finish (DNF)', icon: '❌' },
  { value: 'paused', label: 'Paused / On Hold', icon: '⏸️' },
] as const;

export type SystemShelfValue = typeof SYSTEM_SHELVES[number]['value'];

// Smart mapping patterns
const SYSTEM_SHELF_PATTERNS: Record<SystemShelfValue, string[]> = {
  'to-read': ['tbr', 'to be read', 'want to read', 'to read', 'wishlist', 'want'],
  'reading': ['currently reading', 'reading now', 'in progress', 'started', 'current'],
  'read': ['read', 'finished', 'completed', 'done'],
  'unfinished': ['dnf', 'did not finish', 'abandoned', 'quit', 'gave up', 'unfinished', 'stopped'],
  'paused': ['paused', 'on hold', 'hiatus', 'taking a break', 'postponed', 'shelved'],
};

// Genre patterns
const GENRE_PATTERNS = [
  'romance', 'fantasy', 'scifi', 'sci-fi', 'science fiction', 'mystery', 'thriller',
  'horror', 'historical', 'fiction', 'non-fiction', 'nonfiction', 'biography', 
  'memoir', 'self-help', 'poetry', 'comics', 'manga', 'ya', 'young adult',
  'literary', 'classics', 'contemporary', 'paranormal', 'urban fantasy'
];

export function suggestMapping(shelfName: string): ShelfSuggestion {
  const normalized = shelfName.toLowerCase().replace(/[-_]/g, ' ').trim();
  
  // Check for exact or close matches to system shelves
  for (const [targetShelf, patterns] of Object.entries(SYSTEM_SHELF_PATTERNS)) {
    if (patterns.includes(normalized)) {
      return { type: 'system_shelf', value: targetShelf, confidence: 'high' };
    }
    if (patterns.some(pattern => normalized.includes(pattern) || pattern.includes(normalized))) {
      return { type: 'system_shelf', value: targetShelf, confidence: 'medium' };
    }
  }
  
  // Check for genre patterns → suggest as tag
  if (GENRE_PATTERNS.some(genre => normalized.includes(genre))) {
    return { type: 'tag', value: shelfName, confidence: 'medium' };
  }
  
  // Year-based shelves → custom shelf
  if (/\d{4}/.test(normalized)) {
    return { type: 'custom_shelf', value: shelfName, confidence: 'low' };
  }
  
  // Favorites → custom shelf
  if (['favorites', 'favourites', 'loved', 'starred', '5-stars', 'best'].some(f => normalized.includes(f))) {
    return { type: 'custom_shelf', value: shelfName, confidence: 'medium' };
  }
  
  // Default: suggest custom shelf
  return { type: 'custom_shelf', value: shelfName, confidence: 'low' };
}

export function getSystemShelfLabel(value: string): string {
  const shelf = SYSTEM_SHELVES.find(s => s.value === value);
  return shelf ? shelf.label : value;
}

export function formatMappingType(type: ShelfMapping['targetType']): string {
  switch (type) {
    case 'system_shelf': return 'System Shelf';
    case 'custom_shelf': return 'Custom Shelf';
    case 'tag': return 'Tag';
    case 'skip': return 'Skip';
    default: return type;
  }
}
